import { useEffect, useState } from 'react';
import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { ProgressBars } from '@domgen/base-design-system-2.0/molecules/ProgressBars';
import { Input } from '@domgen/base-design-system-2.0/atoms/Input';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { ArrowLeft } from '../assets/icons/ArrowLeft';
import { StepKey } from '../enums/stepKey';
import { useFormValidation } from '../hooks/useFormValidation';
import { formValidators } from '../utils/form/validators';
import { CircleCheck } from '../assets/icons/CircleCheck';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { usePostHog } from '@posthog/react';
import { FormKey } from '../enums/formKey';
import { getSystemCode } from '../utils/getSystemCode';
import { useApplianceFormData } from '../store/selectors/useApplianceFormData';
import { useAppState } from '../store/selectors/useAppState';
import { useUrlParams } from '../store/selectors/useUrlParams';
import { store } from '../store/store';
import { formApplianceName } from '../utils/store/formApplianceName';
import { formCategoryName } from '../utils/store/formCategoryName';
import { PageBackgroundWrapper } from '../components/PageBackgroundWrapper';
import { iconPalette } from '../const/iconPalette';
import { StoreEvent } from '../enums/storeEvent';
import { AutoComplete } from '@domgen/shared/enums/autoComplete';

const styles = {
  descriptionList: 'flex flex-col gap-space-100',
  descriptionListItemTitle: 'font-bold',
  descriptionListItem:
    'inline-flex justify-start items-center gap-space-200 py-space-100 px-space-200 bg-neutral-0 border-1 border-neutral-150  rounded-radius-100',
};

type PricePageProps = {
  stepNumber: number;
  totalSteps: number;
};

export function PricePage({ stepNumber, totalSteps }: PricePageProps) {
  const formData = useApplianceFormData();
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const systemCode = getSystemCode(urlParams?.rs);
  const loadedFromParams =
    urlParams?.matchedAppliance &&
    urlParams?.mn === formData[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER];
  const VITE_CLIENT_FRIENDLY_NAME = import.meta.env.VITE_CLIENT_FRIENDLY_NAME;
  const applianceCategory = formCategoryName.get();
  const applianceName = formApplianceName.get();
  const [isFocused, setIsFocused] = useState(false);
  const posthog = usePostHog();

  useEffect(() => {
    gaPageView({
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory,
      appSessionID: session?.id,
      pagename: 'olr-purchase-price',
    });
  }, []);

  const PrefilledAppliance = () => {
    const CategoryIcon =
      iconPalette[formData.applianceDetails?.superCategory?.toLowerCase() || ''] || null;
    return (
      <dl className={styles.descriptionList}>
        <dt className={styles.descriptionListItemTitle}>Product</dt>
        <dd className={styles.descriptionListItem}>
          {CategoryIcon && (
            <div className="flex items-center">
              <CategoryIcon className="max-h-5 max-w-9" />
            </div>
          )}
          {VITE_CLIENT_FRIENDLY_NAME} {applianceName}
        </dd>
      </dl>
    );
  };
  const title = loadedFromParams
    ? 'We have captured your product details. How much did it cost?'
    : 'How much did your product cost?';

  const { formValues, errors, handleChange, validateAll } = useFormValidation(
    {
      [FormKey.PURCHASE_PRICE]: formData[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE] || '',
    },
    {
      [FormKey.PURCHASE_PRICE]: [
        formValidators.required('Price is required'),
        formValidators.numeric('Please enter a valid price'),
        formValidators.minNumber(0.01, 'Please enter a purchase price'),
        formValidators.maxNumber(1000000, 'Please enter a value less than $1,000,000.00'),
      ],
    },
  );
  const handleContinue = () => {
    const isValid = validateAll();

    gaEvent({
      eventCategory: 'Continue button',
      eventAction: 'Continue',
      eventLabel: isValid ? 'success' : 'failed',
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      pagename: 'olr-purchase-price',
      regSystemCode: systemCode,
    });

    posthog.capture('price-continue-cta-clicked');

    if (!isValid) {
      return;
    }

    store.send({
      type: StoreEvent.NEXT,
      data: {
        [StepKey.PURCHASE_PRICE]: {
          [FormKey.PURCHASE_PRICE]: formValues[FormKey.PURCHASE_PRICE],
        },
      },
    });
  };

  const handleBack = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'GoBack CTA',
      eventLabel: 'success',
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      pagename: 'olr-purchase-price',
    });
    posthog.capture('price-back-clicked');
    store.send({ type: StoreEvent.BACK, data: { stepKey: StepKey.PURCHASE_PRICE } });
  };

  const handleBlur = () => {
    setIsFocused(false);
    const value = formValues.purchasePrice;

    if (!value) {
      return;
    }

    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) {
      return;
    }

    // Reject negative values for purchase price instead of formatting them
    if (numericValue < 0) {
      handleChange({ target: { name: 'purchasePrice', value: '' } });
      return;
    }

    const formatted = numericValue.toFixed(2);
    handleChange({ target: { name: 'purchasePrice', value: formatted } });

    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'purchasePrice',
      eventLabel: formatted,
      category: 'registration-pages',
      type: 'STANDARD',
      applianceCategory,
      applianceName,
      appSessionID: session?.id,
      pagename: 'olr-purchase-price',
      regSystemCode: systemCode,
    });
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  return (
    <PageBackgroundWrapper gapSize="400">
      <ProgressBars total={totalSteps} current={stepNumber} />

      <h1 className="my-space-400) text-h3">{title}</h1>
      <p className="mb-space-400)">
        This is an estimate of the price you paid in store or online, to the nearest dollar.
      </p>

      <PrefilledAppliance />

      <div className="flex flex-col gap-2">
        <div className="[&_input]:rounded-radius-100">
          <Input
            label="Purchase price"
            placeholder={isFocused ? '' : '0,000'}
            type="number"
            leftIcon="$"
            name="purchasePrice"
            value={formValues.purchasePrice}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            hasError={errors['purchasePrice']}
            rightIcon={
              formValues.purchasePrice && !errors.purchasePrice ? <CircleCheck /> : undefined
            }
            hasSuccess={formValues.purchasePrice !== '' && !errors.purchasePrice}
            autoComplete={AutoComplete.OFF}
          />
        </div>
      </div>

      <div className="flex flex-col w-full mt-4 items-center md:flex-row-reverse md:justify-between">
        <div className="w-full md:w-fit mb-4">
          <Button onClick={handleContinue}>Continue</Button>
        </div>
        <TextLink onClick={handleBack} leftIcon={<ArrowLeft />}>
          Go Back
        </TextLink>
      </div>
    </PageBackgroundWrapper>
  );
}
